import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as AgentAvatarIcon } from '@elementary/common-ui/assets/agent-avatar.svg';
import { ReactComponent as AgentAvatarAnimatedInIcon } from '@elementary/common-ui/assets/agent-avatar-animated-in.svg';
import { ReactComponent as AgentAvatarAnimatedOutIcon } from '@elementary/common-ui/assets/agent-avatar-animated-out.svg';
import { ReactComponent as AgentAvatarPanelAnimatedLeftIcon } from '@elementary/common-ui/assets/agent-avatar-panel-animated-left.svg';
import { ReactComponent as AgentAvatarPanelLeaveIcon } from '@elementary/common-ui/assets/agent-avatar-panel-animated-leave.svg';
import { ReactComponent as AgentAvatarPanelReturnIcon } from '@elementary/common-ui/assets/agent-avatar-panel-animated-return.svg';
import { IconButton } from '@elementary/common-ui/components';
import { HEADER_HEIGHT } from '@elementary/common-ui/constants';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';

const StyledButton = styled(IconButton)`
  width: ${HEADER_HEIGHT}px;
  min-height: ${HEADER_HEIGHT}px;

  &:hover {
    background: none;
  }
`;

type AvatarState =
  | 'initial'
  | 'hovered'
  | 'idle'
  | 'panelOpening'
  | 'panelOpen'
  | 'panelClosing';

const AgentAvatarButton = () => {
  const {
    isExpanded: isAiPanelExpanded,
    openPanel,
    closePanel,
  } = useAiChatPanel();

  const [avatarState, setAvatarState] = useState<AvatarState>(
    isAiPanelExpanded ? 'panelOpen' : 'initial',
  );
  const prevPanelExpanded = useRef(isAiPanelExpanded);

  useEffect(() => {
    if (isAiPanelExpanded !== prevPanelExpanded.current) {
      setAvatarState(isAiPanelExpanded ? 'panelOpening' : 'panelClosing');
      prevPanelExpanded.current = isAiPanelExpanded;
    }
  }, [isAiPanelExpanded]);

  useEffect(() => {
    if (avatarState !== 'panelOpening') return;
    const timer = setTimeout(() => setAvatarState('panelOpen'), 300);
    return () => clearTimeout(timer);
  }, [avatarState]);

  return (
    <StyledButton
      disableRipple
      onClick={isAiPanelExpanded ? closePanel : () => openPanel()}
      onMouseEnter={() => {
        if (!isAiPanelExpanded) setAvatarState('hovered');
      }}
      onMouseLeave={() => {
        if (avatarState === 'panelClosing') {
          setAvatarState('initial');
        } else if (!isAiPanelExpanded) {
          setAvatarState('idle');
        }
      }}
    >
      {avatarState === 'initial' && <AgentAvatarIcon />}
      {avatarState === 'hovered' && <AgentAvatarAnimatedInIcon />}
      {avatarState === 'idle' && <AgentAvatarAnimatedOutIcon />}
      {avatarState === 'panelOpening' && <AgentAvatarPanelLeaveIcon />}
      {avatarState === 'panelOpen' && <AgentAvatarPanelAnimatedLeftIcon />}
      {avatarState === 'panelClosing' && <AgentAvatarPanelReturnIcon />}
    </StyledButton>
  );
};

export default AgentAvatarButton;
